import { useState } from "react";
import { Card } from "@tremor/react";
import {
  Search, AlertTriangle, Check, Clock, X, ChevronRight,
} from "lucide-react";
import {
  useInspectorSourceData, useInspectorRow, useInspectorSourcesSummary,
  useProperties,
  type SourceDataRow, type SourceDataFilters,
} from "@/lib/queries";
import { cn } from "@/lib/utils";


export default function Inspector() {
  const [filters, setFilters] = useState<SourceDataFilters>({
    days_back: 7,
    limit: 100,
    offset: 0,
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const summary = useInspectorSourcesSummary(filters.days_back ?? 7);
  const list = useInspectorSourceData(filters);
  const properties = useProperties(true);

  const setF = (patch: Partial<SourceDataFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Inspector</h1>
        <p className="text-sm text-muted-foreground">
          Every external API call Auriel has made — what was asked, what came
          back, when, and whether it errored. The trust-layer view: every
          claim Auriel surfaces in audits/reports/chat is built on these rows.
          Click a row to see the full response payload.
        </p>
      </div>

      {/* Per-source summary chips */}
      <div className="flex flex-wrap gap-2">
        {summary.data?.sources.map((s) => {
          const isActive = filters.source === s.source;
          return (
            <button
              key={s.source}
              type="button"
              onClick={() => setF({ source: isActive ? undefined : s.source })}
              className={cn(
                "rounded-md border px-3 py-2 text-left transition-colors",
                isActive
                  ? "border-primary bg-primary/10"
                  : "border-border bg-card hover:bg-accent",
              )}
            >
              <div className="flex items-center gap-2 text-xs font-medium">
                <span className="font-mono">{s.source}</span>
                {s.errors > 0 && (
                  <span className="inline-flex items-center gap-0.5 rounded px-1 text-[10px] text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-2.5 w-2.5" />
                    {s.errors}
                  </span>
                )}
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                <span>{s.total.toLocaleString()} calls</span>
                <span>·</span>
                <span>{s.properties_covered} prop</span>
              </div>
              {s.last_fetched_at && (
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  last: {formatDateAgo(s.last_fetched_at)}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[150px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="filter endpoint…"
              value={filters.endpoint_like ?? ""}
              onChange={(e) => setF({ endpoint_like: e.target.value || undefined })}
              className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs"
            />
          </div>
          <div className="relative flex-1 min-w-[150px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="filter target (URL/domain)…"
              value={filters.target_like ?? ""}
              onChange={(e) => setF({ target_like: e.target.value || undefined })}
              className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs"
            />
          </div>
          <select
            value={filters.property_id ?? ""}
            onChange={(e) => setF({ property_id: e.target.value ? Number(e.target.value) : undefined })}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-xs"
          >
            <option value="">All properties</option>
            {properties.data?.items.map((p) => (
              <option key={p.id} value={p.id}>{p.domain}</option>
            ))}
          </select>
          <select
            value={filters.has_error === undefined ? "" : String(filters.has_error)}
            onChange={(e) => {
              const v = e.target.value;
              setF({ has_error: v === "" ? undefined : v === "true" });
            }}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-xs"
          >
            <option value="">All statuses</option>
            <option value="false">Successes only</option>
            <option value="true">Errors only</option>
          </select>
          <select
            value={filters.days_back ?? 7}
            onChange={(e) => setF({ days_back: Number(e.target.value) })}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-xs"
          >
            <option value={1}>last 1 day</option>
            <option value={7}>last 7 days</option>
            <option value={30}>last 30 days</option>
            <option value={90}>last 90 days</option>
          </select>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[2fr_3fr]">
        {/* List */}
        <Card className="overflow-hidden p-0">
          <div className="max-h-[70vh] overflow-y-auto">
            <table className="min-w-full text-xs">
              <thead className="sticky top-0 border-b border-border bg-card text-left">
                <tr>
                  <th className="px-2 py-1.5 font-medium text-muted-foreground">When</th>
                  <th className="px-2 py-1.5 font-medium text-muted-foreground">Source</th>
                  <th className="px-2 py-1.5 font-medium text-muted-foreground">Endpoint</th>
                  <th className="px-2 py-1.5 font-medium text-muted-foreground">Target</th>
                  <th className="px-2 py-1.5 font-medium text-muted-foreground"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.isLoading && (
                  <tr><td colSpan={5} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>
                )}
                {list.data?.items.map((row) => (
                  <Row
                    key={row.id}
                    row={row}
                    selected={selectedId === row.id}
                    onClick={() => setSelectedId(row.id)}
                  />
                ))}
                {list.data && list.data.items.length === 0 && (
                  <tr><td colSpan={5} className="px-2 py-4 text-center text-muted-foreground">No rows match the filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {list.data && list.data.total > (filters.limit ?? 100) && (
            <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/30 px-3 py-2 text-xs">
              <span className="text-muted-foreground">
                {(filters.offset ?? 0) + 1}–{Math.min((filters.offset ?? 0) + (filters.limit ?? 100), list.data.total)} of {list.data.total.toLocaleString()}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={(filters.offset ?? 0) === 0}
                  onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, (f.offset ?? 0) - (f.limit ?? 100)) }))}
                  className="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent disabled:opacity-50"
                >Prev</button>
                <button
                  type="button"
                  disabled={(filters.offset ?? 0) + (filters.limit ?? 100) >= list.data.total}
                  onClick={() => setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + (f.limit ?? 100) }))}
                  className="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent disabled:opacity-50"
                >Next</button>
              </div>
            </div>
          )}
        </Card>

        {/* Detail panel */}
        <DetailPanel rowId={selectedId} />
      </div>
    </div>
  );
}


function Row({ row, selected, onClick }: {
  row: SourceDataRow; selected: boolean; onClick: () => void;
}) {
  return (
    <tr
      onClick={onClick}
      className={cn(
        "cursor-pointer hover:bg-accent/40",
        selected && "bg-primary/10 hover:bg-primary/15",
      )}
    >
      <td className="whitespace-nowrap px-2 py-1.5 align-top text-muted-foreground">
        <Clock className="mr-1 inline h-3 w-3" />
        {formatDateAgo(row.fetched_at)}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 align-top">
        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">{row.source}</span>
      </td>
      <td className="px-2 py-1.5 align-top font-mono text-[11px]">
        <div className="flex items-center gap-1">
          {row.has_error
            ? <X className="h-3 w-3 shrink-0 text-red-600 dark:text-red-400" />
            : <Check className="h-3 w-3 shrink-0 text-emerald-700 dark:text-emerald-400" />
          }
          <span className="truncate">{row.endpoint}</span>
        </div>
      </td>
      <td className="max-w-[200px] truncate px-2 py-1.5 align-top font-mono text-[11px]" title={row.target}>
        {row.target}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 align-top text-right text-[10px] text-muted-foreground">
        {formatBytes(row.response_size_bytes)}
        <ChevronRight className="ml-1 inline h-3 w-3" />
      </td>
    </tr>
  );
}


function DetailPanel({ rowId }: { rowId: number | null }) {
  const detail = useInspectorRow(rowId);

  if (rowId === null) {
    return (
      <Card>
        <div className="text-sm text-muted-foreground">
          Click a row to inspect the full response payload.
        </div>
      </Card>
    );
  }

  if (detail.isLoading) {
    return <Card><div className="text-sm text-muted-foreground">Loading…</div></Card>;
  }

  if (!detail.data) {
    return <Card><div className="text-sm text-red-600">Row not found.</div></Card>;
  }

  const d = detail.data;

  return (
    <Card>
      <div className="space-y-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Header
          </div>
          <div className="mt-1 grid grid-cols-2 gap-2 text-xs">
            <KV k="ID"           v={d.id.toString()} />
            <KV k="Source"       v={d.source} mono />
            <KV k="Endpoint"     v={d.endpoint} mono />
            <KV k="Target"       v={d.target} mono />
            <KV k="Property ID"  v={d.property_id?.toString() ?? "—"} />
            <KV k="Audit ID"     v={d.audit_id?.toString() ?? "—"} />
            <KV k="Fetched at"   v={d.fetched_at} />
            <KV k="Cost units"   v={d.cost_units?.toString() ?? "—"} />
            <KV k="Response size" v={formatBytes(d.response_size_bytes)} />
            <KV k="Status"        v={d.has_error ? "ERROR" : "OK"} />
          </div>
        </div>

        {d.error && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-red-600 dark:text-red-400">
              Error
            </div>
            <pre className="mt-1 overflow-x-auto rounded bg-red-500/10 p-2 font-mono text-[11px] text-red-700 dark:text-red-400">{d.error}</pre>
          </div>
        )}

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Params
          </div>
          <pre className="mt-1 max-h-32 overflow-auto rounded bg-muted/40 p-2 font-mono text-[11px]">{JSON.stringify(d.params, null, 2)}</pre>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Response payload
            </div>
            {d.response !== null && (
              <button
                type="button"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(JSON.stringify(d.response, null, 2)); }
                  catch { /* ignore */ }
                }}
                className="rounded-md border border-border bg-background px-2 py-0.5 text-[10px] hover:bg-accent"
              >Copy JSON</button>
            )}
          </div>
          <pre className="mt-1 max-h-[400px] overflow-auto rounded bg-muted/40 p-2 font-mono text-[11px]">{
            d.response === null ? "(null — request failed before response)" : JSON.stringify(d.response, null, 2)
          }</pre>
        </div>
      </div>
    </Card>
  );
}


function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="shrink-0 text-muted-foreground">{k}:</span>
      <span className={cn("truncate", mono && "font-mono")} title={v}>{v}</span>
    </div>
  );
}


function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}


function formatDateAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h`;
  return `${Math.round(sec / 86400)}d`;
}
